import React from 'react';
import * as stylex from '@stylexjs/stylex';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorInformation } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Gap } from '../theme/Spaces.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight } from '../theme/Fonts.stylex';
import { t } from 'src/primitives/I18n';
const formatCurrency = (value: number, fractionDigits: number) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: fractionDigits,
  maximumFractionDigits: fractionDigits
}).format(value);
const SummaryAmountsContext = React.createContext({
  fractionDigits: 3
});

// MARK: exported types
// ====================
export type SummaryAmountsLineVariant = 'default' | 'subtle' | 'strong' | 'info';

// MARK: Line
// ==========
type SummaryAmountsLineProps = {
  variant?: SummaryAmountsLineVariant;
  label: string;
  value?: number;
  valueElement?: React.ReactNode;
};
function SummaryAmountsLineComponent({
  variant = 'default',
  label,
  value,
  valueElement
}: SummaryAmountsLineProps) {
  const {
    fractionDigits
  } = React.useContext(SummaryAmountsContext);
  let content: React.ReactNode = null;
  if (valueElement != null) {
    content = typeof valueElement === 'number' ? formatCurrency(valueElement, fractionDigits) : valueElement;
  } else if (value != null) {
    content = formatCurrency(value, fractionDigits);
  }
  return <div className="display-xjp7ctv" data-style-src="wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:149">
      <span {...stylex.props(styles.Line_text, styles.Line_label, variantLineStyles[variant])}>
        {label + t(':')}
      </span>
      <span {...stylex.props(styles.Line_text, styles.Line_value, variantLineStyles[variant])}>
        {content}
      </span>
    </div>;
}
const MemoizedSummaryAmountsLineComponent = React.memo(SummaryAmountsLineComponent);
export { MemoizedSummaryAmountsLineComponent as SummaryAmountsLine };
export type { SummaryAmountsLineProps };

// MARK: Gap
// =========
function SummaryAmountsGapComponent() {
  return <div className="gridColumn-x1osaytk height-x1kmlbfa" data-style-src="wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:172" />;
}
const MemoizedSummaryAmountsGapComponent = React.memo(SummaryAmountsGapComponent);
export { MemoizedSummaryAmountsGapComponent as SummaryAmountsGap };

// MARK: Caption
// ==============
type SummaryAmountsCaptionProps = {
  children: React.ReactNode;
};
function SummaryAmountsCaptionComponent({
  children
}: SummaryAmountsCaptionProps) {
  return <div className="gridColumn-x1osaytk fontFamily-x111x8ze fontSize-x1vcisby lineHeight-x1ttk3gv color-xt4ixol" data-style-src="wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:176">{children}</div>;
}
const MemoizedSummaryAmountsCaptionComponent = React.memo(SummaryAmountsCaptionComponent);
export { MemoizedSummaryAmountsCaptionComponent as SummaryAmountsCaption };
export type { SummaryAmountsCaptionProps };

// MARK: root
// ==========
type SummaryAmountsProps = {
  children: React.ReactNode;
  fractionDigits: number;
};
function SummaryAmountsComponent({
  children,
  fractionDigits
}: SummaryAmountsProps) {
  const contextValue = React.useMemo(() => ({
    fractionDigits
  }), [fractionDigits]);
  return <SummaryAmountsContext.Provider value={contextValue}>
      <div className="display-xrvj5dj gridTemplateColumns-xaf1lus columnGap-x4m2q02 rowGap-x1rdo6bc alignItems-xpqajaz width-xeq5yr9 maxWidth-x193iq5w" data-style-src="wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:140">{children}</div>
    </SummaryAmountsContext.Provider>;
}
const MemoizedSummaryAmountsComponent = React.memo(SummaryAmountsComponent);
export { MemoizedSummaryAmountsComponent as SummaryAmounts };
export type { SummaryAmountsProps };

// MARK: style
// ===========
const variantLineStyles = {
  default: {
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:123"
  },
  subtle: {
    "color-kMwMTN": "color-xt4ixol",
    "fontSize-kGuDYH": "fontSize-x1egw0ox",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:126"
  },
  strong: {
    "color-kMwMTN": "color-x1w1nkkj",
    "fontWeight-k63SB2": "fontWeight-x5x31i3",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:130"
  },
  info: {
    "color-kMwMTN": "color-xx67xh0",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:134"
  }
};
const styles = {
  Line_text: {
    "fontFamily-kMv6JI": "fontFamily-x111x8ze",
    "fontWeight-k63SB2": "fontWeight-x1fkahbb",
    "fontSize-kGuDYH": "fontSize-x1vcisby",
    "lineHeight-kLWn49": "lineHeight-x1ttk3gv",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:152"
  },
  Line_label: {
    "textAlign-k9WMMc": "textAlign-xdpxx8g",
    "overflowWrap-kHjlTd": "overflowWrap-xj0a0fe",
    "maxHeight-kskxy": "maxHeight-xe41u3v",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:158"
  },
  Line_value: {
    "display-k1xSpc": "display-x78zum5",
    "justifyContent-kjj79g": "justifyContent-x7v6yn8",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "textAlign-k9WMMc": "textAlign-x1hr2gdg",
    "whiteSpace-khDVqt": "whiteSpace-xuxw1ft",
    "fontVariantNumeric-kcqcaj": "fontVariantNumeric-xss6m8b",
    "maxHeight-kskxy": "maxHeight-xe41u3v",
    $$css: "wino-gc:src/resources/data-visualizations/CanarySummaryAmounts.tsx:163"
  }
};